import { apiFetch } from './client';
import { useAuth } from '@/lib/auth-store';

export type AuthListKind = 'blacklist' | 'allowlist' | 'pin_list' | 'cid_pin_list';

export interface AuthListRow {
  id: string;
  reseller_id: string;
  customer_id: string;
  /**
   * Customer-scoped sequential id (migration 0068). Surfaced as `AL-${n}`
   * in URLs and the portal UI. The list lookup endpoints accept either
   * the UUID or the AL-N form transparently.
   */
  ref_number: number;
  name: string;
  kind: AuthListKind;
  description: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  entry_count: number;
}

/**
 * Format a list ref_number for display + use in URLs.
 *   formatListRef(42)  →  "AL-42"
 */
export function formatListRef(n: number): string {
  return `AL-${n}`;
}

export interface AuthListEntryRow {
  id: string;
  list_id: string;
  e164: string | null;
  has_pin: boolean;
  label: string | null;
  /** Migration 0068 — long-form notes (≤2000 chars). Distinct from `label`. */
  description: string | null;
  /** Migration 0067 — manual on/off without soft-deleting. */
  active: boolean;
  /** Migration 0067 — optional validity window. Both null = always valid. */
  start_date: string | null;
  end_date: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

export interface CreateAuthListBody {
  name: string;
  kind: AuthListKind;
  description?: string | null;
  customer_id?: string;
  reseller_id?: string;
}

export interface PatchAuthListBody {
  name?: string;
  kind?: AuthListKind;
  description?: string | null;
}

export interface CreateAuthListEntryBody {
  e164?: string | null;
  pin?: string | null;
  label?: string | null;
  description?: string | null;
  active?: boolean;
  start_date?: string | null;
  end_date?: string | null;
}

export interface PatchAuthListEntryBody {
  e164?: string | null;
  pin?: string | null;
  clear_pin?: boolean;
  label?: string | null;
  description?: string | null;
  active?: boolean;
  start_date?: string | null;
  end_date?: string | null;
}

export interface BulkImportResult {
  total: number;
  created: number;
  errors: ReadonlyArray<{ row: number; reason: string }>;
}

export const authLists = {
  list: (p: { kind?: AuthListKind; customer_id?: string } = {}) => {
    const q = new URLSearchParams();
    if (p.kind) q.set('kind', p.kind);
    if (p.customer_id) q.set('customer_id', p.customer_id);
    const qs = q.toString();
    return apiFetch<{ rows: AuthListRow[] }>(
      `/api/auth-lists${qs ? `?${qs}` : ''}`,
    );
  },
  get: (id: string) =>
    apiFetch<{ list: AuthListRow; entries: AuthListEntryRow[]; next_cursor: string | null }>(
      `/api/auth-lists/${id}`,
    ),
  create: (body: CreateAuthListBody) =>
    apiFetch<AuthListRow>('/api/auth-lists', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  update: (id: string, patch: PatchAuthListBody) =>
    apiFetch<AuthListRow>(`/api/auth-lists/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),
  remove: (id: string) =>
    apiFetch<void>(`/api/auth-lists/${id}`, { method: 'DELETE' }),

  /**
   * Paginated entry list. Supports both cursor (created_at) and offset
   * pagination plus an optional `search` query (substring match on label /
   * e164). Returns `total` so the page UI can render "showing N of M".
   */
  listEntries: (
    listId: string,
    p: { limit?: number; cursor?: string; offset?: number; search?: string } = {},
  ) => {
    const q = new URLSearchParams();
    if (p.limit) q.set('limit', String(p.limit));
    if (p.cursor) q.set('cursor', p.cursor);
    if (typeof p.offset === 'number') q.set('offset', String(p.offset));
    if (p.search) q.set('search', p.search);
    const qs = q.toString();
    return apiFetch<{
      rows: AuthListEntryRow[];
      next_cursor: string | null;
      total: number;
    }>(`/api/auth-lists/${listId}/entries${qs ? `?${qs}` : ''}`);
  },
  createEntry: (listId: string, body: CreateAuthListEntryBody) =>
    apiFetch<AuthListEntryRow>(`/api/auth-lists/${listId}/entries`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  updateEntry: (listId: string, entryId: string, patch: PatchAuthListEntryBody) =>
    apiFetch<AuthListEntryRow>(`/api/auth-lists/${listId}/entries/${entryId}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),
  removeEntry: (listId: string, entryId: string) =>
    apiFetch<void>(`/api/auth-lists/${listId}/entries/${entryId}`, {
      method: 'DELETE',
    }),

  /**
   * Bulk-update a set of entries. Currently only `active` is patchable
   * (the "select rows + activate/deactivate" UI). Server caps at 1000 ids
   * per call.
   */
  bulkUpdateEntries: (
    listId: string,
    body: { ids: string[]; patch: { active?: boolean } },
  ) =>
    apiFetch<{ updated: number }>(
      `/api/auth-lists/${listId}/bulk-update-entries`,
      { method: 'PATCH', body: JSON.stringify(body) },
    ),

  /** Bulk soft-delete. Same id cap (1000) as bulk-update. */
  bulkDeleteEntries: (listId: string, ids: string[]) =>
    apiFetch<{ deleted: number }>(
      `/api/auth-lists/${listId}/entries/bulk-delete`,
      { method: 'POST', body: JSON.stringify({ ids }) },
    ),

  /**
   * Trigger a browser download of the sample CSV. Can't use a plain
   * `<a href download>` because the endpoint requires a Bearer token and
   * `<a>` doesn't carry one. We fetch authenticated, build a Blob, and
   * synthesize an `<a download>` click.
   */
  downloadSampleCsv: () => downloadAuthorizedFile(
    '/api/auth-lists/sample.csv',
    'auth-list-sample.csv',
  ),

  /** Trigger a browser download of all entries for `listId`. */
  downloadEntriesCsv: (listId: string, suggestedName?: string) =>
    downloadAuthorizedFile(
      `/api/auth-lists/${listId}/entries.csv`,
      suggestedName ?? `auth-list-${listId}-entries.csv`,
    ),

  /**
   * Bulk-import a CSV file. The file's text is read on the client and POSTed
   * as JSON `{csv: "..."}` rather than multipart so we don't need a
   * file-upload parser server-side. 5MB cap server-side.
   */
  bulkImportEntries: (listId: string, csv: string) =>
    apiFetch<BulkImportResult>(
      `/api/auth-lists/${listId}/entries/bulk-import`,
      { method: 'POST', body: JSON.stringify({ csv }) },
    ),
};

/**
 * Download a server-side file that requires Bearer auth, by fetching
 * with the auth header attached and synthesizing an `<a download>` click
 * on a Blob URL.
 *
 * Why this exists: `<a href="/api/..." download>` doesn't send the
 * Authorization header, so the browser hits the endpoint anonymously and
 * gets a 401. We can't put the token in the URL either (it'd leak via
 * referer/logs/history). The Blob-URL pattern is the standard fix.
 *
 * Honors the server's `Content-Disposition: attachment; filename=...`
 * when present (overrides `suggestedName`); falls back otherwise.
 */
async function downloadAuthorizedFile(
  url: string,
  suggestedName: string,
): Promise<void> {
  const accessToken = useAuth.getState().accessToken;
  const res = await fetch(url, {
    headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
    credentials: 'include',
  });
  if (!res.ok) {
    throw new Error(`download_failed_${res.status}`);
  }
  // Prefer the server-suggested filename if it provided one.
  const cd = res.headers.get('Content-Disposition') ?? '';
  const m = /filename="?([^"]+)"?/.exec(cd);
  const filename = m?.[1] || suggestedName;

  const blob = await res.blob();
  const blobUrl = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    // Defer revoke so Safari has a chance to start the download.
    setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
  }
}
